import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Form, Button, Spinner, Row, Col, Table, Alert } from 'react-bootstrap';
import { FaPlus, FaTrash } from 'react-icons/fa';
import LookupLayout from './LookupLayout';
import { theme } from '../../theme/theme.js';
import Validator from '../../utils/validator';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import PicklistApi from '../../api/PicklistApi';
import { runCustomScriptPayload } from '../../api/CustomScriptApi';
import './css/EditLayout.css';

const SCRIPT_KEY_OR_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Map view_page (form JSON) field entries to EditLayout-compatible field objects. */
export function normalizeDynamicField(raw) {
  const fieldName = String(raw?.name ?? raw?.fieldName ?? '').trim();
  if (!fieldName) return null;
  const label = raw.label != null ? String(raw.label) : fieldName;
  const type = String(raw.type || 'text').toLowerCase();
  const isRequired =
    raw.required === true ||
    raw.required === 'true' ||
    raw.isRequired === true ||
    raw.isRequired === 'true';
  /** Omit `readonly` / `isReadonly` (or set false) on editable fields. `readonly: true` disables inputs—including `select`+`optionsFromFormLoad` (script-driven dropdowns). */
  const isReadonly = raw.isReadonly === true || raw.readonly === true;
  const dc = raw.display_config ?? raw.displayConfig ?? null;
  const ps = raw.picklistSource ?? raw.picklist_source;
  let picklistSource = null;
  if (ps && typeof ps === 'object' && !Array.isArray(ps)) {
    const on = ps.objectName ?? ps.object_name;
    const pfn = ps.fieldName ?? ps.field_name;
    if (on && pfn) {
      picklistSource = {
        objectName: String(on).trim(),
        fieldName: String(pfn).trim(),
      };
    }
  }
  return {
    fieldName,
    label,
    type,
    isRequired,
    isReadonly,
    placeholder: raw.placeholder != null ? String(raw.placeholder) : undefined,
    referenceObject: raw.referenceObject || raw.reference_object || null,
    lookupSearchFields: raw.lookupSearchFields || raw.lookup_search_fields || 'name',
    displayFields: raw.displayFields || raw.display_fields || 'name',
    lookupFilter: raw.lookupFilter ?? raw.lookup_filter,
    precision: raw.precision != null ? Number(raw.precision) : undefined,
    scale: raw.scale != null ? Number(raw.scale) : undefined,
    columnSpan: raw.columnSpan === 2 || raw.column_span === 2 ? 2 : 1,
    displayConfig: dc,
    currencySymbol: raw.currencySymbol || raw.currency_symbol || '\u20B9',
    options: Array.isArray(raw.options) ? raw.options : null,
    picklistSource,
    accept: raw.accept != null ? String(raw.accept) : null,
    maxSizeMb:
      raw.maxSizeMb != null
        ? Number(raw.maxSizeMb)
        : raw.max_size_mb != null
          ? Number(raw.max_size_mb)
          : null,
    optionsFromFormLoad:
      raw.optionsFromFormLoad === true ||
      raw.options_from_form_load === true ||
      raw.optionsFromScript === true ||
      raw.options_from_script === true,
    emptyOptionsMessage:
      raw.emptyOptionsMessage !== undefined && raw.emptyOptionsMessage !== null
        ? String(raw.emptyOptionsMessage)
        : raw.empty_options_message !== undefined && raw.empty_options_message !== null
          ? String(raw.empty_options_message)
          : undefined,
  };
}

const DEFAULT_EMPTY_LOAD_OPTIONS_MESSAGE =
  'No rows returned for this list. Check the load script query and child object field names for your tenant.';

function getEmptyLoadOptionsMessage(field, config) {
  if (field?.emptyOptionsMessage !== undefined && field?.emptyOptionsMessage !== null) {
    return String(field.emptyOptionsMessage);
  }
  const c = config?.emptyLoadOptionsMessage ?? config?.empty_load_options_message;
  if (c !== undefined && c !== null) return String(c);
  return DEFAULT_EMPTY_LOAD_OPTIONS_MESSAGE;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('File read failed.'));
    reader.onload = () => {
      const out = String(reader.result || '');
      const comma = out.indexOf(',');
      resolve(comma >= 0 ? out.slice(comma + 1) : out);
    };
    reader.readAsDataURL(file);
  });
}

function isFileValuePopulated(v) {
  return !!(v && typeof v === 'object' && !Array.isArray(v) && typeof v.base64 === 'string' && v.base64);
}

function getAllowNegative(field) {
  if (!field) return true;
  const dc = field.displayConfig;
  if (dc == null || dc === undefined) return true;
  const config =
    typeof dc === 'string'
      ? (() => {
          try {
            return JSON.parse(dc);
          } catch {
            return {};
          }
        })()
      : dc || {};
  return config.allow_negative !== false;
}

function validatePhoneNumber(phoneNumber) {
  if (!phoneNumber) return true;
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  if (digitsOnly.length === 0) return false;
  if (digitsOnly.length < 10) return false;
  if (digitsOnly.length > 15) return false;
  const phonePattern =
    /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,5}[-\s.]?[0-9]{1,4}$/;
  return phonePattern.test(phoneNumber);
}

function extractLookupFilter(field) {
  let lookupFilterArray = null;
  let lookupFilterLogic = 'AND';
  const lookupFilter = field.lookupFilter || field.lookup_filter;
  if (!lookupFilter) {
    return { lookupFilterArray, lookupFilterLogic };
  }
  if (typeof lookupFilter === 'string') {
    try {
      const parsed = JSON.parse(lookupFilter);
      if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed.filters) {
        lookupFilterArray = Array.isArray(parsed.filters) ? parsed.filters : null;
        lookupFilterLogic =
          parsed.logic && (parsed.logic.toUpperCase() === 'OR' || parsed.logic.toUpperCase() === 'AND')
            ? parsed.logic.toUpperCase()
            : 'AND';
      } else if (Array.isArray(parsed)) {
        lookupFilterArray = parsed;
      }
    } catch {
      /* ignore */
    }
  } else if (typeof lookupFilter === 'object' && !Array.isArray(lookupFilter) && lookupFilter.filters) {
    lookupFilterArray = Array.isArray(lookupFilter.filters) ? lookupFilter.filters : null;
    lookupFilterLogic =
      lookupFilter.logic &&
      (lookupFilter.logic.toUpperCase() === 'OR' || lookupFilter.logic.toUpperCase() === 'AND')
        ? lookupFilter.logic.toUpperCase()
        : 'AND';
  } else if (Array.isArray(lookupFilter)) {
    lookupFilterArray = lookupFilter;
  }
  return { lookupFilterArray, lookupFilterLogic };
}

function scopeRecordviewToObjectNameForField(field, layoutObjectName) {
  const ref = String(field.referenceObject || '').toLowerCase().trim();
  const fn = String(field.fieldName || '').toLowerCase();
  const isRecordviewLookup =
    ref === 'recordview' || fn === 'recordviewid' || fn === 'recordview_id';
  if (!isRecordviewLookup || !layoutObjectName || !String(layoutObjectName).trim()) {
    return undefined;
  }
  return String(layoutObjectName).trim();
}

function buildInitialValues(fields) {
  const values = {};
  const lookupLabels = {};
  fields.forEach((f) => {
    values[f.fieldName] = '';
    lookupLabels[f.fieldName] = '';
  });
  return { values, lookupLabels };
}

/** Pre-fill form state from action propMappings (only keys that exist on the form). */
function mergeFieldInitials(baseValues, initial) {
  if (!initial || typeof initial !== 'object' || Array.isArray(initial)) {
    return { ...baseValues };
  }
  const out = { ...baseValues };
  Object.keys(initial).forEach((k) => {
    if (!Object.prototype.hasOwnProperty.call(out, k)) return;
    const v = initial[k];
    if (v != null && String(v) !== '') {
      out[k] = String(v);
    }
  });
  return out;
}

function repeatRowErrorKey(rowIdx, fieldName) {
  return `__r${rowIdx}__${fieldName}`;
}

/** Map API / script row object into repeatable row state (values + lookupLabels). */
function mapApiRowToRepeatRow(apiRow, rowTemplateFields) {
  const base = buildInitialValues(rowTemplateFields);
  if (!apiRow || typeof apiRow !== 'object') return base;
  rowTemplateFields.forEach((f) => {
    const k = f.fieldName;
    const v = apiRow[k];
    base.values[k] = v != null && String(v) !== '' ? String(v) : '';
  });
  return base;
}

function rowHasAnyVisibleValue(row, rowTemplateFields) {
  return rowTemplateFields.some((f) => {
    if (f.type === 'hidden') return false;
    return String(row.values[f.fieldName] ?? '').trim() !== '';
  });
}

/** Include row in submit payload if any visible field is set, or a hidden field (e.g. id) is set. */
function repeatRowObjectShouldSubmit(obj, rowTemplateFields) {
  const hasVisible = rowTemplateFields.some((f) => {
    if (f.type === 'hidden') return false;
    return String(obj[f.fieldName] ?? '').trim() !== '';
  });
  const hasHidden = rowTemplateFields.some(
    (f) => f.type === 'hidden' && String(obj[f.fieldName] ?? '').trim() !== ''
  );
  return hasVisible || hasHidden;
}

function buildFieldRows(fieldsList, sectionColumns) {
  const rows = [];
  let currentRow = [];
  let currentRowWidth = 0;
  fieldsList.forEach((field) => {
    const fieldWidth =
      field.columnSpan === 2
        ? Math.ceil(12 / sectionColumns) * 2
        : Math.ceil(12 / sectionColumns);
    if (currentRowWidth + fieldWidth > 12) {
      if (currentRow.length) rows.push([...currentRow]);
      currentRow = [];
      currentRowWidth = 0;
    }
    currentRow.push(field);
    currentRowWidth += fieldWidth;
  });
  if (currentRow.length) rows.push(currentRow);
  return rows;
}

const labelStyle = {
  fontFamily: theme.typography.fontFamily.primary,
  fontWeight: theme.typography.fontWeight.medium,
  color: theme.colors.textSecondary,
  fontSize: theme.typography.fontSize.lg,
};

const controlStyle = {
  fontFamily: theme.typography.fontFamily.primary,
  fontSize: theme.typography.fontSize.lg,
};

/**
 * Dynamic form from view_page custom_script config.code (type: "form"), styled like EditLayout.
 * @param {{
 *   config: {
 *     title?: string,
 *     layoutColumns?: number,
 *     fields?: Array<Record<string, unknown> & {
 *       picklistSource?: { objectName: string, fieldName: string },
 *       emptyOptionsMessage?: string,
 *     }>,
 *     introMessage?: string,
 *     intro_message?: string,
 *     submit?: {
 *       actionScriptId?: string,
 *       buttonLabel?: string,
 *       buttonAlign?: 'left' | 'right' | 'center' | 'middle',
 *       disableIfNoLoadOptions?: boolean,
 *       autoRun?: boolean,
 *       auto_run?: boolean,
 *       hideButton?: boolean,
 *       hide_button?: boolean,
 *       showCloseButton?: boolean,
 *       closeButtonLabel?: string,
 *       statusMessage?: string,
 *     },
 *     repeatable?: boolean,
 *     rowFields?: Array<Record<string, unknown>>,
 *     minRows?: number,
 *     maxRows?: number,
 *     rowSubmitKey?: string,
 *     addRowLabel?: string,
 *     removeRowLabel?: string,
 *     rowSectionLabel?: string,
 *     repeatableLayout?: 'table' | 'sections',
 *     loadRowsScriptKey?: string,
 *     loadRowsScriptId?: string,
 *     loadFormScriptKey?: string,
 *     loadFormScriptId?: string,
 *     loadFormOnOpen?: boolean,
 *     emptyLoadOptionsMessage?: string,
 *     // Load script may return `formFieldOverrides` / `form_field_overrides`: array of raw field
 *     // objects (same shape as config.fields). When non-empty, they replace the form fields
 *     // for this record (e.g. readonly label + hidden id instead of a select).
 *   },
 *   onSubmit: (values: Record<string, string>) => void | Promise<void>,
 *   submitting?: boolean,
 *   objectName?: string|null,
 *   recordId?: string|null,
 *   initialValues?: Record<string, unknown>|null,
 *   onModalClose?: (() => void) | null,
 *   submitFeedbackBanner?: { variant?: string, message?: string } | null,
 * }} props
 */
export default function DynamicForm({
  config,
  onSubmit,
  submitting = false,
  objectName = null,
  recordId = null,
  result = null,
  initialValues = null,
  onModalClose = null,
  submitFeedbackBanner = null,
}) {
  const fields = useMemo(() => {
    const raw = Array.isArray(config?.fields) ? config.fields : [];
    return raw.map(normalizeDynamicField).filter(Boolean);
  }, [config?.fields]);

  const loadFormOnOpen =
    config?.loadFormOnOpen === true || config?.load_form_on_open === true;

  const loadFormLoadingLabel = useMemo(() => {
    if (
      config?.loadFormLoadingMessage != null &&
      String(config.loadFormLoadingMessage).trim() !== ''
    ) {
      return String(config.loadFormLoadingMessage);
    }
    if (
      config?.load_form_loading_message != null &&
      String(config.load_form_loading_message).trim() !== ''
    ) {
      return String(config.load_form_loading_message);
    }
    return 'Loading form…';
  }, [config?.loadFormLoadingMessage, config?.load_form_loading_message]);

  /** When loadFormScript returns `formFieldOverrides`, those fields replace `config.fields` for rendering. */
  const [loadFormFieldOverrides, setLoadFormFieldOverrides] = useState(null);

  const effectiveFields = useMemo(() => {
    if (Array.isArray(loadFormFieldOverrides) && loadFormFieldOverrides.length > 0) {
      return loadFormFieldOverrides.map(normalizeDynamicField).filter(Boolean);
    }
    return fields;
  }, [loadFormFieldOverrides, fields]);

  const introMessage =
    config?.introMessage != null && String(config.introMessage).trim() !== ''
      ? String(config.introMessage).trim()
      : config?.intro_message != null && String(config.intro_message).trim() !== ''
        ? String(config.intro_message).trim()
        : '';

  const rowTemplateFields = useMemo(() => {
    const rep = config?.repeatable === true || config?.repeatable === 'true';
    if (!rep) return [];
    const raw = config?.rowFields ?? config?.row_fields;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map(normalizeDynamicField).filter(Boolean);
  }, [config?.repeatable, config?.rowFields, config?.row_fields]);

  const visibleRepeatFields = useMemo(
    () => rowTemplateFields.filter((f) => f.type !== 'hidden'),
    [rowTemplateFields]
  );

  const isRepeatable = rowTemplateFields.length > 0;

  const sectionColumns = config?.layoutColumns ?? config?.layout_columns ?? 2;

  const minRep = useMemo(
    () => Math.max(1, parseInt(String(config?.minRows ?? config?.min_rows ?? 1), 10) || 1),
    [config?.minRows, config?.min_rows]
  );
  const maxRep = useMemo(() => {
    const m = parseInt(String(config?.maxRows ?? config?.max_rows ?? 50), 10);
    const cap = Number.isFinite(m) ? m : 50;
    return Math.min(100, Math.max(minRep, cap));
  }, [config?.maxRows, config?.max_rows, minRep]);

  const picklistFields = isRepeatable ? rowTemplateFields : effectiveFields;

  const [{ values, lookupLabels }, setFormState] = useState(() => buildInitialValues(fields));
  const [errors, setErrors] = useState({});
  const [dynamicPicklistState, setDynamicPicklistState] = useState({});
  const [repeatRows, setRepeatRows] = useState([]);
  const [loadFormOptionsByField, setLoadFormOptionsByField] = useState({});
  const [loadFormScriptLoading, setLoadFormScriptLoading] = useState(false);
  const [loadFormScriptError, setLoadFormScriptError] = useState(null);
  const autoRunOnceRef = useRef(false);
  // Allows a loadFormScript to short-circuit the form: when set, the fields and
  // submit button are hidden and only this message is rendered (e.g. opportunity
  // already converted → nothing to fill in, just tell the user).
  const [loadFormDisabled, setLoadFormDisabled] = useState(null);

  const dynamicPicklistDescriptors = useMemo(
    () =>
      picklistFields
        .filter((f) => (f.type === 'picklist' || f.type === 'select') && f.picklistSource)
        .map((f) => ({
          formFieldName: f.fieldName,
          objectName: f.picklistSource.objectName,
          picklistFieldName: f.picklistSource.fieldName,
        })),
    [picklistFields]
  );

  useEffect(() => {
    if (isRepeatable) return;
    if (Array.isArray(loadFormFieldOverrides) && loadFormFieldOverrides.length > 0) {
      return;
    }
    const base = buildInitialValues(fields);
    const mergedValues = mergeFieldInitials(base.values, initialValues);
    setFormState({ values: mergedValues, lookupLabels: base.lookupLabels });
    setErrors({});
  }, [config, fields, isRepeatable, initialValues, loadFormFieldOverrides]);

  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsLoadError, setRowsLoadError] = useState(null);

  useEffect(() => {
    if (!isRepeatable) {
      setRepeatRows([]);
      setRowsLoadError(null);
      setRowsLoading(false);
      return undefined;
    }

    const loadKeyRaw =
      config?.loadRowsScriptKey ??
      config?.load_rows_script_key ??
      config?.loadRowsScriptId ??
      config?.load_rows_script_id;
    const loadKey = loadKeyRaw != null ? String(loadKeyRaw).trim() : '';
    const rid = recordId != null ? String(recordId).trim() : '';
    const on = objectName != null ? String(objectName).trim() : '';

    let cancelled = false;
    const initEmpty = () =>
      Array.from({ length: minRep }, () => buildInitialValues(rowTemplateFields));

    (async () => {
      if (!loadKey || !rid || !on) {
        setRowsLoading(false);
        setRowsLoadError(null);
        setRepeatRows(initEmpty());
        setErrors({});
        return;
      }

      setRowsLoading(true);
      setRowsLoadError(null);
      try {
        const isUuid = SCRIPT_KEY_OR_ID_UUID_RE.test(loadKey);
        const res = await runCustomScriptPayload({
          scriptKey: isUuid ? undefined : loadKey,
          customScriptId: isUuid ? loadKey : undefined,
          recordId: rid,
          objectName: on,
          inputData: { __dynamicFormLoadRows: true },
        });
        if (cancelled) return;
        if (!res?.success) {
          throw new Error(res?.message || 'Failed to load rows.');
        }
        const vm = res.data;
        if (vm && typeof vm === 'object' && vm.success === false) {
          throw new Error(vm.message || 'Failed to load rows.');
        }
        const rawRows = Array.isArray(vm?.rows) ? vm.rows : [];
        let mapped = rawRows.map((r) => mapApiRowToRepeatRow(r, rowTemplateFields));
        if (mapped.length < minRep) {
          while (mapped.length < minRep) {
            mapped.push(buildInitialValues(rowTemplateFields));
          }
        }
        setRepeatRows(mapped);
        setErrors({});
      } catch (e) {
        if (!cancelled) {
          setRowsLoadError(e?.message || String(e));
          setRepeatRows(initEmpty());
          setErrors({});
        }
      } finally {
        if (!cancelled) setRowsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isRepeatable, rowTemplateFields, minRep, config, recordId, objectName]);

  /** Non-repeatable forms: call backend script once to fill select options (e.g. child records). */
  useEffect(() => {
    if (isRepeatable) {
      setLoadFormOptionsByField({});
      setLoadFormScriptLoading(false);
      setLoadFormScriptError(null);
      setLoadFormDisabled(null);
      setLoadFormFieldOverrides(null);
      return undefined;
    }
    const loadKeyRaw =
      config?.loadFormScriptKey ??
      config?.load_form_script_key ??
      config?.loadFormScriptId ??
      config?.load_form_script_id;
    const loadKey = loadKeyRaw != null ? String(loadKeyRaw).trim() : '';
    const hasLoadField = fields.some((f) => f.optionsFromFormLoad);
    if (!loadKey || (!hasLoadField && !loadFormOnOpen)) {
      setLoadFormOptionsByField({});
      setLoadFormScriptLoading(false);
      setLoadFormScriptError(null);
      setLoadFormDisabled(null);
      setLoadFormFieldOverrides(null);
      return undefined;
    }
    const rid = recordId != null ? String(recordId).trim() : '';
    const on = objectName != null ? String(objectName).trim() : '';
    if (!rid || !on) {
      setLoadFormOptionsByField({});
      setLoadFormScriptLoading(false);
      setLoadFormScriptError(null);
      setLoadFormDisabled(null);
      setLoadFormFieldOverrides(null);
      return undefined;
    }
    let cancelled = false;
    setLoadFormScriptLoading(true);
    setLoadFormScriptError(null);
    setLoadFormOptionsByField({});
    setLoadFormFieldOverrides(null);
    setLoadFormDisabled(null);
    (async () => {
      try {
        const isUuid = SCRIPT_KEY_OR_ID_UUID_RE.test(loadKey);
        const res = await runCustomScriptPayload({
          scriptKey: isUuid ? undefined : loadKey,
          customScriptId: isUuid ? loadKey : undefined,
          recordId: rid,
          objectName: on,
          inputData: { __dynamicFormLoadForm: true },
        });
        if (cancelled) return;
        if (!res?.success) {
          throw new Error(res?.message || 'Failed to load form options.');
        }
        let vm = res.data;
        if (
          vm == null &&
          res &&
          typeof res === 'object' &&
          (res.disableForm === true || res.disable_form === true)
        ) {
          vm = res;
        }
        if (vm && typeof vm === 'object' && vm.success === false) {
          throw new Error(vm.message || 'Failed to load form options.');
        }
        // Optional "disable form" short-circuit: when the load script returns
        // disableForm: true, render only the message (no fields, no submit).
        const disableFormFlag =
          vm &&
          typeof vm === 'object' &&
          (vm.disableForm === true || vm.disable_form === true);
        if (disableFormFlag) {
          const msg =
            vm.disableFormMessage ??
            vm.disable_form_message ??
            vm.message ??
            '';
          const variantRaw =
            vm.disableFormVariant ??
            vm.disable_form_variant ??
            'info';
          setLoadFormDisabled({
            message: msg != null ? String(msg) : '',
            variant: String(variantRaw || 'info').trim().toLowerCase(),
          });
          setLoadFormOptionsByField({});
          setLoadFormFieldOverrides(null);
          return;
        }
        const rawMap = vm?.optionsByField ?? vm?.options_by_field;
        const next = {};
        if (rawMap && typeof rawMap === 'object' && !Array.isArray(rawMap)) {
          Object.keys(rawMap).forEach((k) => {
            const arr = rawMap[k];
            next[k] = Array.isArray(arr) ? arr : [];
          });
        }
        setLoadFormOptionsByField(next);
        const loadInitials =
          vm?.initialValues ??
          vm?.initial_values ??
          vm?.formInitialValues ??
          vm?.form_initial_values;
        const overridesRaw = vm?.formFieldOverrides ?? vm?.form_field_overrides;
        if (Array.isArray(overridesRaw) && overridesRaw.length > 0) {
          setLoadFormFieldOverrides(overridesRaw);
          const ef = overridesRaw.map(normalizeDynamicField).filter(Boolean);
          const base = buildInitialValues(ef);
          const mergedVals =
            loadInitials && typeof loadInitials === 'object' && !Array.isArray(loadInitials)
              ? mergeFieldInitials(base.values, loadInitials)
              : base.values;
          setFormState({
            values: mergedVals,
            lookupLabels: base.lookupLabels,
          });
        } else {
          setLoadFormFieldOverrides(null);
          if (
            loadInitials &&
            typeof loadInitials === 'object' &&
            !Array.isArray(loadInitials)
          ) {
            setFormState((prev) => ({
              ...prev,
              values: mergeFieldInitials(prev.values, loadInitials),
            }));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setLoadFormScriptError(e?.message || String(e));
          setLoadFormOptionsByField({});
          setLoadFormFieldOverrides(null);
        }
      } finally {
        if (!cancelled) setLoadFormScriptLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isRepeatable, config, fields, recordId, objectName, loadFormOnOpen]);

  useEffect(() => {
    const ac = new AbortController();
    const targets = dynamicPicklistDescriptors;
    if (targets.length === 0) {
      setDynamicPicklistState({});
      return undefined;
    }
    setDynamicPicklistState(
      Object.fromEntries(
        targets.map((t) => [t.formFieldName, { loading: true, options: [], error: null }])
      )
    );
    const picklistApi = new PicklistApi();
    targets.forEach((t) => {
      (async () => {
        try {
          const ocRes = await ObjectConfigApi.fetchConfigObjectByNameWithFields(t.objectName);
          if (ac.signal.aborted) return;
          const cfg = ocRes?.data;
          const flist = Array.isArray(cfg?.fields) ? cfg.fields : [];
          const needle = t.picklistFieldName.toLowerCase();
          const fieldMeta = flist.find(
            (x) => String(x.field_name || x.fieldName || '').toLowerCase() === needle
          );
          if (!fieldMeta?.id) {
            if (!ac.signal.aborted) {
              setDynamicPicklistState((prev) => ({
                ...prev,
                [t.formFieldName]: {
                  loading: false,
                  options: [],
                  error: 'Picklist field not found on object.',
                },
              }));
            }
            return;
          }
          const dt = String(fieldMeta.data_type || fieldMeta.dataType || '').toLowerCase();
          if (dt !== 'picklist' && dt !== 'multipicklist') {
            if (!ac.signal.aborted) {
              setDynamicPicklistState((prev) => ({
                ...prev,
                [t.formFieldName]: {
                  loading: false,
                  options: [],
                  error: 'Field is not a picklist.',
                },
              }));
            }
            return;
          }
          const plRes = await picklistApi.getActivePicklistValues(fieldMeta.id, {
            signal: ac.signal,
          });
          if (ac.signal.aborted) return;
          const body = plRes?.data;
          const rawVals = Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body)
              ? body
              : [];
          const options = rawVals
            .map((item) => {
              const v = item?.value_key ?? item?.valueKey ?? item?.key ?? item?.value ?? '';
              const lab =
                item?.value_label ?? item?.valueLabel ?? item?.label ?? item?.value ?? v;
              const val = v != null && String(v).trim() !== '' ? String(v).trim() : '';
              if (!val) return null;
              return { value: val, label: String(lab || val).trim() || val };
            })
            .filter(Boolean);
          setDynamicPicklistState((prev) => ({
            ...prev,
            [t.formFieldName]: { loading: false, options, error: null },
          }));
        } catch (e) {
          if (ac.signal.aborted || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
            return;
          }
          setDynamicPicklistState((prev) => ({
            ...prev,
            [t.formFieldName]: {
              loading: false,
              options: [],
              error: e?.message || 'Failed to load picklist.',
            },
          }));
        }
      })();
    });
    return () => ac.abort();
  }, [dynamicPicklistDescriptors]);

  const setValue = useCallback((fieldName, v) => {
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, [fieldName]: v },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const setLookupValue = useCallback((fieldName, id, label) => {
    setFormState((prev) => ({
      values: { ...prev.values, [fieldName]: id != null ? String(id) : '' },
      lookupLabels: { ...(prev.lookupLabels || {}), [fieldName]: label != null ? String(label) : '' },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const setRepeatRowValue = useCallback((rowIdx, fieldName, v) => {
    setRepeatRows((prev) =>
      prev.map((row, i) =>
        i === rowIdx ? { ...row, values: { ...row.values, [fieldName]: v } } : row
      )
    );
    setErrors((prev) => {
      const next = { ...prev };
      delete next[repeatRowErrorKey(rowIdx, fieldName)];
      return next;
    });
  }, []);

  const setRepeatRowLookupValue = useCallback((rowIdx, fieldName, id, label) => {
    setRepeatRows((prev) =>
      prev.map((row, i) =>
        i === rowIdx
          ? {
              ...row,
              values: { ...row.values, [fieldName]: id != null ? String(id) : '' },
              lookupLabels: {
                ...(row.lookupLabels || {}),
                [fieldName]: label != null ? String(label) : '',
              },
            }
          : row
      )
    );
    setErrors((prev) => {
      const next = { ...prev };
      delete next[repeatRowErrorKey(rowIdx, fieldName)];
      return next;
    });
  }, []);

  const computeFieldError = useCallback((field, rawVal) => {
    if (field.isReadonly) return '';
    if (field.type === 'hidden') return '';

    if (field.type === 'file') {
      if (field.isRequired && !isFileValuePopulated(rawVal)) {
        return `${field.label} is required.`;
      }
      return '';
    }

    const strVal = rawVal === null || rawVal === undefined ? '' : String(rawVal);

    if (field.isRequired && !strVal.trim()) {
      return `${field.label} is required.`;
    }
    if (!strVal) return '';

    const t = field.type;
    if (t === 'email' && !Validator.isEmail(strVal)) {
      return 'Invalid email format.';
    }
    if (t === 'url' && !Validator.isURL(strVal)) {
      return 'Invalid URL.';
    }
    if ((t === 'phone' || t === 'tel') && !validatePhoneNumber(strVal)) {
      return 'Invalid phone number. Please enter a valid number with at least 10 digits.';
    }
    if (['number', 'decimal', 'float', 'currency'].includes(t)) {
      const numValue = Number(strVal);
      if (Number.isNaN(numValue)) {
        return 'Invalid numeric value.';
      }
      const allowNegative = getAllowNegative(field);
      if (!allowNegative && numValue < 0) {
        return `${field.label} does not allow negative values.`;
      }
      if (field.precision) {
        const validation = Validator.validateNumericPrecision(
          numValue,
          field.precision,
          field.scale || 0
        );
        if (!validation.isValid) return validation.message;
      }
    }
    return '';
  }, []);

  const validateField = useCallback(
    (field) => computeFieldError(field, values[field.fieldName]),
    [computeFieldError, values]
  );

  const validateAll = useCallback(() => {
    if (isRepeatable) {
      const next = {};
      const anyRowHasData = repeatRows.some((row) => rowHasAnyVisibleValue(row, rowTemplateFields));
      if (!anyRowHasData) {
        const firstVisible = visibleRepeatFields[0] || rowTemplateFields[0];
        if (firstVisible) {
          next[repeatRowErrorKey(0, firstVisible.fieldName)] = 'Enter at least one row of data.';
        }
        setErrors(next);
        return false;
      }
      repeatRows.forEach((row, rowIdx) => {
        const hasAny = rowHasAnyVisibleValue(row, rowTemplateFields);
        if (!hasAny) return;
        rowTemplateFields.forEach((f) => {
          const err = computeFieldError(f, row.values[f.fieldName]);
          if (err) next[repeatRowErrorKey(rowIdx, f.fieldName)] = err;
        });
      });
      setErrors(next);
      return Object.keys(next).length === 0;
    }
    const plain = {};
    effectiveFields.forEach((f) => {
      const err = validateField(f);
      if (err) plain[f.fieldName] = err;
    });
    setErrors(plain);
    return Object.keys(plain).length === 0;
  }, [
    isRepeatable,
    repeatRows,
    rowTemplateFields,
    effectiveFields,
    validateField,
    computeFieldError,
    visibleRepeatFields,
  ]);

  const addRepeatRow = useCallback(() => {
    setRepeatRows((prev) => {
      if (prev.length >= maxRep) return prev;
      return [...prev, buildInitialValues(rowTemplateFields)];
    });
  }, [maxRep, rowTemplateFields]);

  const removeRepeatRow = useCallback((rowIdx) => {
    setRepeatRows((prev) => {
      if (prev.length <= minRep) return prev;
      return prev.filter((_, i) => i !== rowIdx);
    });
    setErrors({});
  }, [minRep]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateAll()) return;
    if (onSubmit) {
      if (isRepeatable) {
        const key =
          String(config?.rowSubmitKey ?? config?.row_submit_key ?? 'rows').trim() || 'rows';
        const cleaned = repeatRows
          .map((r) => ({ ...r.values }))
          .filter((obj) => repeatRowObjectShouldSubmit(obj, rowTemplateFields));
        onSubmit({ [key]: cleaned });
      } else {
        onSubmit({ ...values });
      }
    }
  };

  const renderInput = (field, ctx = null) => {
    const rowValues = ctx ? ctx.values : values;
    const rowLookupLabels = ctx ? ctx.lookupLabels : lookupLabels;
    const errKey = ctx ? repeatRowErrorKey(ctx.rowIndex, field.fieldName) : field.fieldName;
    const fieldError = errors[errKey];
    const validationClass = fieldError ? 'is-invalid' : '';
    const value = rowValues[field.fieldName] ?? '';
    const disabled = field.isReadonly || submitting;
    const { lookupFilterArray, lookupFilterLogic } = extractLookupFilter(field);
    const patchValue = (fn, v) =>
      ctx ? setRepeatRowValue(ctx.rowIndex, fn, v) : setValue(fn, v);
    const patchLookup = (fn, id, label) =>
      ctx ? setRepeatRowLookupValue(ctx.rowIndex, fn, id, label) : setLookupValue(fn, id, label);

    switch (field.type) {
      case 'hidden':
        return (
          <input
            type="hidden"
            name={field.fieldName}
            value={value}
            onChange={(e) => patchValue(field.fieldName, e.target.value)}
          />
        );
      case 'textarea':
      case 'richtext':
        return (
          <>
            <Form.Control
              name={field.fieldName}
              as="textarea"
              rows={field.type === 'richtext' ? 5 : 3}
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              placeholder={field.placeholder}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'lookup':
        if (!field.referenceObject) {
          return (
            <div className="text-muted small">
              Lookup requires <code>referenceObject</code> in the field definition.
            </div>
          );
        }
        return (
          <>
            <LookupLayout
              objectName={field.referenceObject}
              searchFields={field.lookupSearchFields}
              displayFields={field.displayFields}
              value={value}
              defaultLabel={(rowLookupLabels && rowLookupLabels[field.fieldName]) || ''}
              onChange={(id, label) => patchLookup(field.fieldName, id, label)}
              disabled={disabled}
              isRequired={field.isRequired}
              lookupFilter={lookupFilterArray}
              lookupFilterLogic={lookupFilterLogic}
              lookupFilterFieldValues={rowValues}
              scopeRecordviewToObjectName={scopeRecordviewToObjectNameForField(field, objectName)}
              rootClassName={validationClass}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'boolean':
      case 'checkbox':
        return (
          <>
            <Form.Check
              id={
                ctx
                  ? `dynamic-form-r${ctx.rowIndex}-${field.fieldName}`
                  : `dynamic-form-${field.fieldName}`
              }
              type="checkbox"
              label={field.placeholder || field.label}
              checked={value === true || value === 'true' || value === '1'}
              disabled={disabled}
              onChange={(ev) => patchValue(field.fieldName, ev.target.checked ? 'true' : '')}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'picklist':
      case 'select': {
        const usesFormLoad = Boolean(field.optionsFromFormLoad);
        if (usesFormLoad) {
          if (loadFormScriptLoading) {
            return (
              <div className="text-muted small d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" role="status" />
                Loading options…
              </div>
            );
          }
          if (loadFormScriptError) {
            return <div className="text-danger small">{loadFormScriptError}</div>;
          }
          const scriptOpts = loadFormOptionsByField[field.fieldName];
          const options = Array.isArray(scriptOpts) ? scriptOpts : [];
          if (!options.length) {
            const emptyMsg = getEmptyLoadOptionsMessage(field, config);
            if (!String(emptyMsg).trim()) return null;
            return <div className="text-muted small">{emptyMsg}</div>;
          }
          return (
            <>
              <Form.Select
                value={value}
                disabled={disabled}
                className={validationClass}
                onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
                style={controlStyle}
              >
                <option value="">{field.placeholder || `Select ${field.label}`}</option>
                {options.map((opt, i) => {
                  const v = opt?.value != null ? String(opt.value) : String(opt?.id ?? '');
                  const lab = opt?.label != null ? String(opt.label) : v;
                  return (
                    <option key={`${v}-${i}`} value={v}>
                      {lab}
                    </option>
                  );
                })}
              </Form.Select>
              {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
            </>
          );
        }
        const usesDynamic = Boolean(field.picklistSource);
        const dyn = dynamicPicklistState[field.fieldName];
        if (usesDynamic) {
          if (!dyn || dyn.loading) {
            return (
              <div className="text-muted small d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" role="status" />
                Loading options…
              </div>
            );
          }
          if (dyn.error) {
            return <div className="text-danger small">{dyn.error}</div>;
          }
        }
        const options = usesDynamic
          ? dyn.options || []
          : Array.isArray(field.options)
            ? field.options
            : [];
        if (!options.length) {
          return (
            <div className="text-muted small">
              {usesDynamic ? (
                'No active picklist values for this field.'
              ) : (
                <>
                  Use an <code>options</code> array, or{' '}
                  <code>picklistSource</code> with <code>objectName</code> and <code>fieldName</code>{' '}
                  (tenant picklist).
                </>
              )}
            </div>
          );
        }
        return (
          <>
            <Form.Select
              value={value}
              disabled={disabled}
              className={validationClass}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              style={controlStyle}
            >
              <option value="">{field.placeholder || `Select ${field.label}`}</option>
              {options.map((opt, i) => {
                const v = opt?.value != null ? String(opt.value) : String(opt?.id ?? '');
                const lab = opt?.label != null ? String(opt.label) : v;
                return (
                  <option key={`${v}-${i}`} value={v}>
                    {lab}
                  </option>
                );
              })}
            </Form.Select>
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );
      }

      case 'date':
        return (
          <>
            <Form.Control
              type="date"
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'datetime':
        return (
          <>
            <Form.Control
              type="datetime-local"
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'time':
        return (
          <>
            <Form.Control
              type="time"
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'number':
      case 'decimal':
      case 'float': {
        const precision = field.precision;
        const scale = field.scale || 0;
        const maxValue = precision ? Math.pow(10, precision - scale) - 1 : null;
        const allowNegativeNumber = getAllowNegative(field);
        const minValue =
          maxValue !== null
            ? allowNegativeNumber
              ? -maxValue
              : 0
            : allowNegativeNumber
              ? null
              : 0;

        return (
          <>
            <Form.Control
              type="number"
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              placeholder={field.placeholder || 'Enter number'}
              min={minValue}
              max={maxValue}
              step={scale > 0 ? Math.pow(10, -scale) : 1}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );
      }

      case 'currency': {
        const currencyScale = field.scale != null ? field.scale : 2;
        const currencyPrecision = field.precision;
        const currencyMaxValue = currencyPrecision
          ? Math.pow(10, currencyPrecision - currencyScale) - 1
          : null;
        const allowNegativeCurrency = getAllowNegative(field);
        const currencyMinValue =
          currencyMaxValue !== null
            ? allowNegativeCurrency
              ? -currencyMaxValue
              : 0
            : allowNegativeCurrency
              ? null
              : 0;

        return (
          <>
            <div className="input-group">
              <span className="input-group-text">{field.currencySymbol}</span>
              <Form.Control
                type="number"
                step={Math.pow(10, -currencyScale)}
                value={value}
                onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
                required={field.isRequired}
                disabled={disabled}
                className={validationClass}
                placeholder={field.placeholder || 'Enter amount'}
                min={currencyMinValue}
                max={currencyMaxValue}
                style={controlStyle}
              />
            </div>
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );
      }

      case 'phone':
      case 'tel':
        return (
          <>
            <Form.Control
              type="tel"
              value={value}
              onChange={(ev) => {
                const v = ev.target.value;
                patchValue(field.fieldName, v);
                if (v && !validatePhoneNumber(v)) {
                  setErrors((prev) => ({
                    ...prev,
                    [errKey]:
                      'Invalid phone number. Please enter a valid number with at least 10 digits.',
                  }));
                }
              }}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              placeholder={field.placeholder || 'Phone'}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'email':
        return (
          <>
            <Form.Control
              type="email"
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              placeholder={field.placeholder}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'url':
        return (
          <>
            <Form.Control
              type="text"
              inputMode="url"
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              placeholder={field.placeholder || 'https://'}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );

      case 'file': {
        const fileVal = rowValues[field.fieldName];
        const hasFile = isFileValuePopulated(fileVal);
        const inputId = `dyn-file-${ctx ? `${ctx.rowIndex}-` : ''}${field.fieldName}`;
        const onChange = async (ev) => {
          const file = ev.target.files && ev.target.files[0];
          ev.target.value = '';
          if (!file) return;

          if (field.maxSizeMb && Number.isFinite(field.maxSizeMb)) {
            const limit = field.maxSizeMb * 1024 * 1024;
            if (file.size > limit) {
              setErrors((prev) => ({
                ...prev,
                [errKey]: `File is too large. Max size is ${field.maxSizeMb} MB.`,
              }));
              return;
            }
          }

          try {
            const base64 = await readFileAsBase64(file);
            patchValue(field.fieldName, {
              name: file.name,
              mimeType: file.type || 'application/octet-stream',
              size: file.size,
              base64,
            });
            setErrors((prev) => {
              const next = { ...prev };
              delete next[errKey];
              return next;
            });
          } catch (err) {
            setErrors((prev) => ({
              ...prev,
              [errKey]: `Could not read file: ${err?.message || err}`,
            }));
          }
        };
        const onRemove = () => patchValue(field.fieldName, null);

        return (
          <>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Control
                id={inputId}
                type="file"
                accept={field.accept || undefined}
                onChange={onChange}
                disabled={disabled}
                className={validationClass}
                style={controlStyle}
              />
              {hasFile ? (
                <div className="d-flex align-items-center gap-2 small text-muted">
                  <span title={fileVal.name}>
                    <i className="bi bi-paperclip me-1"></i>
                    {fileVal.name}
                    {typeof fileVal.size === 'number'
                      ? ` (${(fileVal.size / 1024).toFixed(1)} KB)`
                      : ''}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 text-danger"
                    onClick={onRemove}
                    disabled={disabled}
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </div>
            {field.accept ? (
              <div className="form-text small">Allowed: {field.accept}</div>
            ) : null}
            {field.maxSizeMb ? (
              <div className="form-text small">Max size: {field.maxSizeMb} MB</div>
            ) : null}
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );
      }

      default:
        return (
          <>
            <Form.Control
              type={field.type === 'password' ? 'password' : 'text'}
              value={value}
              onChange={(ev) => patchValue(field.fieldName, ev.target.value)}
              required={field.isRequired}
              disabled={disabled}
              className={validationClass}
              placeholder={field.placeholder}
              style={controlStyle}
            />
            {fieldError ? <div className="invalid-feedback d-block">{fieldError}</div> : null}
          </>
        );
    }
  };

  const submitToolbar = useMemo(() => {
    const s = config?.submit;
    const obj = s && typeof s === 'object' && !Array.isArray(s) ? s : {};
    const label =
      obj.buttonLabel != null && String(obj.buttonLabel).trim() !== ''
        ? String(obj.buttonLabel)
        : obj.label != null && String(obj.label).trim() !== ''
          ? String(obj.label)
          : 'Submit';
    const alignRaw = String(obj.buttonAlign ?? obj.align ?? obj.position ?? 'left')
      .trim()
      .toLowerCase();
    let justify = 'flex-start';
    if (alignRaw === 'right') justify = 'flex-end';
    else if (alignRaw === 'center' || alignRaw === 'middle') justify = 'center';
    return { label, justify };
  }, [config?.submit]);

  /** When false, submit stays enabled even if loadFormScript returned no options (default: apply disable rule). */
  const disableSubmitIfNoLoadOptions = useMemo(() => {
    const s = config?.submit;
    const obj = s && typeof s === 'object' && !Array.isArray(s) ? s : {};
    if (obj.disableIfNoLoadOptions === false || obj.disable_if_no_load_options === false) {
      return false;
    }
    return true;
  }, [config?.submit]);

  const hasLoadFormFields = useMemo(
    () => !isRepeatable && effectiveFields.some((f) => f.optionsFromFormLoad),
    [isRepeatable, effectiveFields]
  );

  /** One banner spinner for loadFormScript; avoid per-field "Loading options…" at the same time. */
  const suppressFieldsWhileLoadFormScript =
    !isRepeatable &&
    loadFormScriptLoading &&
    (hasLoadFormFields || loadFormOnOpen);

  const loadFormSubmitBlocked = useMemo(() => {
    if (!hasLoadFormFields || !disableSubmitIfNoLoadOptions) return false;
    const loadKeyRaw =
      config?.loadFormScriptKey ??
      config?.load_form_script_key ??
      config?.loadFormScriptId ??
      config?.load_form_script_id;
    const loadKey = loadKeyRaw != null ? String(loadKeyRaw).trim() : '';
    if (!loadKey) return false;
    if (loadFormScriptLoading) return true;
    if (loadFormScriptError) return true;
    return effectiveFields.some((f) => {
      if (!f.optionsFromFormLoad) return false;
      const opts = loadFormOptionsByField[f.fieldName];
      return !Array.isArray(opts) || opts.length === 0;
    });
  }, [
    hasLoadFormFields,
    disableSubmitIfNoLoadOptions,
    config,
    effectiveFields,
    loadFormScriptLoading,
    loadFormScriptError,
    loadFormOptionsByField,
  ]);

  const submitConfig = config?.submit;
  const submitObj =
    submitConfig && typeof submitConfig === 'object' && !Array.isArray(submitConfig)
      ? submitConfig
      : {};
  const submitAutoRun = submitObj.autoRun === true || submitObj.auto_run === true;
  const submitHidePrimaryButton =
    submitObj.hideButton === true || submitObj.hide_button === true;
  const submitStatusMessage =
    submitObj.statusMessage != null && String(submitObj.statusMessage).trim() !== ''
      ? String(submitObj.statusMessage).trim()
      : 'Working…';
  /** Secondary footer button (Cancel/Close): shown beside submit when showCloseButton, or alone when hideButton+modal. */
  const showFooterCloseButton =
    typeof onModalClose === 'function' &&
    (submitHidePrimaryButton || submitObj.showCloseButton === true);
  const closeButtonLabelFooter =
    submitObj.closeButtonLabel != null && String(submitObj.closeButtonLabel).trim() !== ''
      ? String(submitObj.closeButtonLabel).trim()
      : 'Close';

  useEffect(() => {
    if (!submitAutoRun || isRepeatable || !onSubmit) {
      return undefined;
    }
    if (loadFormDisabled) {
      return undefined;
    }
    if (loadFormScriptLoading) {
      return undefined;
    }
    if (loadFormScriptError) {
      return undefined;
    }
    if (loadFormSubmitBlocked) {
      return undefined;
    }
    if (autoRunOnceRef.current) {
      return undefined;
    }
    const t = window.setTimeout(() => {
      if (autoRunOnceRef.current) {
        return;
      }
      if (!validateAll()) {
        return;
      }
      autoRunOnceRef.current = true;
      onSubmit({ ...values });
    }, 0);
    return () => clearTimeout(t);
  }, [
    submitAutoRun,
    isRepeatable,
    onSubmit,
    values,
    validateAll,
    loadFormDisabled,
    loadFormScriptLoading,
    loadFormScriptError,
    loadFormSubmitBlocked,
  ]);

  const fieldRows = useMemo(
    () => buildFieldRows(effectiveFields, sectionColumns),
    [effectiveFields, sectionColumns]
  );

  const repeatableSectionsLayout =
    String(config?.repeatableLayout ?? config?.repeatable_layout ?? 'table')
      .trim()
      .toLowerCase() === 'sections';

  const addRowLabel =
    config?.addRowLabel != null && String(config.addRowLabel).trim() !== ''
      ? String(config.addRowLabel)
      : 'Add row';
  const removeRowLabel =
    config?.removeRowLabel != null && String(config.removeRowLabel).trim() !== ''
      ? String(config.removeRowLabel)
      : 'Remove';

  if (loadFormDisabled) {
    const variant =
      ['danger', 'warning', 'success', 'info', 'primary', 'secondary'].includes(
        loadFormDisabled.variant
      )
        ? loadFormDisabled.variant
        : 'info';
    const msg =
      loadFormDisabled.message && String(loadFormDisabled.message).trim() !== ''
        ? loadFormDisabled.message
        : 'This form is not available.';
    return (
      <div className="edit-layout-dynamic-form">
        <Alert variant={variant} className="mb-0">
          {msg}
        </Alert>
        {typeof onModalClose === 'function' ? (
          <div className="mt-3 d-flex justify-content-end">
            <Button type="button" variant="outline-secondary" onClick={onModalClose}>
              Close
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  const feedbackBannerVariantRaw =
    submitFeedbackBanner?.variant != null
      ? String(submitFeedbackBanner.variant).trim().toLowerCase()
      : '';
  const feedbackBannerVariant = [
    'danger',
    'warning',
    'success',
    'info',
    'primary',
    'secondary',
  ].includes(feedbackBannerVariantRaw)
    ? feedbackBannerVariantRaw
    : 'danger';
  const feedbackBannerMessage =
    submitFeedbackBanner?.message != null &&
    String(submitFeedbackBanner.message).trim() !== ''
      ? String(submitFeedbackBanner.message)
      : '';

  return (
    <div className="edit-layout-dynamic-form">
      <Form onSubmit={handleSubmit}>
        {feedbackBannerMessage ? (
          <Alert variant={feedbackBannerVariant} className="mb-3" role="alert">
            {feedbackBannerMessage}
          </Alert>
        ) : null}
        {introMessage ? (
          <Alert variant="info" className="mb-3" role="status">
            {introMessage}
          </Alert>
        ) : null}
        {config?.title ? (
          <div className="form-section mb-3">
            <h3
              className="section-title"
              style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.textPrimary,
                fontSize: theme.typography.fontSize['2xl'],
              }}
            >
              {config.title}
            </h3>
            <div className="section-divider" />
          </div>
        ) : null}

        <div className="form-section">
          <div className="section-content">
            {!isRepeatable && loadFormScriptLoading && (hasLoadFormFields || loadFormOnOpen) ? (
              <div
                className="d-flex align-items-center gap-2 text-muted mb-3"
                role="status"
                aria-live="polite"
              >
                <Spinner animation="border" size="sm" aria-hidden="true" />
                <span>{loadFormLoadingLabel}</span>
              </div>
            ) : null}
            {!isRepeatable && loadFormOnOpen && loadFormScriptError && !hasLoadFormFields ? (
              <p className="text-danger small mb-3" role="alert">
                {loadFormScriptError}
              </p>
            ) : null}
            {isRepeatable && rowsLoading ? (
              <div className="d-flex align-items-center gap-2 text-muted mb-3">
                <Spinner animation="border" size="sm" role="status" />
                <span>Loading rows…</span>
              </div>
            ) : null}
            {isRepeatable && rowsLoadError ? (
              <p className="text-danger small mb-3">{rowsLoadError}</p>
            ) : null}
            {isRepeatable
              ? repeatableSectionsLayout
                ? (() => {
                    const rowSectionLabel =
                      config?.rowSectionLabel != null && String(config.rowSectionLabel).trim() !== ''
                        ? String(config.rowSectionLabel)
                        : 'Row';
                    const repeatFieldRows = buildFieldRows(visibleRepeatFields, sectionColumns);
                    return repeatRows.map((row, repIdx) => (
                      <div
                        key={`repeat-block-${repIdx}`}
                        className="border rounded p-3 mb-3"
                        style={{ background: 'var(--bs-tertiary-bg, rgba(0,0,0,0.03))' }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="small text-muted fw-semibold">
                            {rowSectionLabel} {repIdx + 1}
                          </span>
                          {repeatRows.length > minRep ? (
                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeRepeatRow(repIdx)}
                              disabled={submitting || rowsLoading}
                            >
                              <FaTrash className="me-1" />
                              {removeRowLabel}
                            </Button>
                          ) : null}
                        </div>
                        {repeatFieldRows.map((rowFields, gridIdx) => (
                          <Row className="form-row" key={`rep-${repIdx}-grid-${gridIdx}`}>
                            {rowFields.map((field, fIdx) => {
                              const colSize =
                                field.columnSpan === 2
                                  ? Math.ceil(12 / sectionColumns) * 2
                                  : Math.ceil(12 / sectionColumns);
                              const errK = repeatRowErrorKey(repIdx, field.fieldName);
                              return (
                                <Col key={field.fieldName || fIdx} md={colSize} className="field-column">
                                  <div className={`form-field ${errors[errK] ? 'has-error' : ''}`}>
                                    {field.type !== 'checkbox' && field.type !== 'boolean' ? (
                                      <Form.Label className="field-label" style={labelStyle}>
                                        {field.label}
                                        {field.isRequired ? (
                                          <span className="required-asterisk">*</span>
                                        ) : null}
                                      </Form.Label>
                                    ) : null}
                                    <div className="field-input">
                                      {renderInput(field, {
                                        rowIndex: repIdx,
                                        values: row.values,
                                        lookupLabels: row.lookupLabels || {},
                                      })}
                                    </div>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        ))}
                      </div>
                    ));
                  })()
                : (() => {
                    const showRemoveCol = repeatRows.length > minRep;
                    return (
                      <div className="table-responsive mb-3">
                        <Table bordered hover size="sm" className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              {visibleRepeatFields.map((field) => (
                                <th key={field.fieldName} style={{ ...labelStyle, fontWeight: 600 }}>
                                  {field.label}
                                  {field.isRequired ? (
                                    <span className="required-asterisk">*</span>
                                  ) : null}
                                </th>
                              ))}
                              {showRemoveCol ? (
                                <th
                                  className="text-end"
                                  style={{ width: '1%', whiteSpace: 'nowrap' }}
                                  aria-label="Actions"
                                >
                                  {' '}
                                </th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {repeatRows.map((row, repIdx) => (
                              <tr key={`repeat-row-${repIdx}`}>
                                {visibleRepeatFields.map((field) => {
                                  const errK = repeatRowErrorKey(repIdx, field.fieldName);
                                  return (
                                    <td
                                      key={field.fieldName}
                                      className={errors[errK] ? 'table-warning' : undefined}
                                    >
                                      <div className="field-input">
                                        {renderInput(field, {
                                          rowIndex: repIdx,
                                          values: row.values,
                                          lookupLabels: row.lookupLabels || {},
                                        })}
                                      </div>
                                    </td>
                                  );
                                })}
                                {showRemoveCol ? (
                                  <td className="text-end align-middle">
                                    <Button
                                      type="button"
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => removeRepeatRow(repIdx)}
                                      disabled={submitting || rowsLoading}
                                    >
                                      <FaTrash className="me-1" />
                                      {removeRowLabel}
                                    </Button>
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    );
                  })()
              : suppressFieldsWhileLoadFormScript
                ? null
                : fieldRows.map((rowFields, rowIdx) => (
                  <Row className="form-row" key={`row-${rowIdx}`}>
                    {rowFields.map((field, fIdx) => {
                      const colSize =
                        field.columnSpan === 2
                          ? Math.ceil(12 / sectionColumns) * 2
                          : Math.ceil(12 / sectionColumns);
                      return (
                        <Col
                          key={field.fieldName || fIdx}
                          md={field.type === 'hidden' ? 12 : colSize}
                          className={`field-column${field.type === 'hidden' ? ' d-none' : ''}`}
                        >
                          <div className={`form-field ${errors[field.fieldName] ? 'has-error' : ''}`}>
                            {field.type !== 'checkbox' &&
                            field.type !== 'boolean' &&
                            field.type !== 'hidden' ? (
                              <Form.Label className="field-label" style={labelStyle}>
                                {field.label}
                                {field.isRequired ? (
                                  <span className="required-asterisk">*</span>
                                ) : null}
                              </Form.Label>
                            ) : null}
                            <div className="field-input">{renderInput(field)}</div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                ))}
            {isRepeatable && repeatRows.length < maxRep ? (
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="mb-3"
                onClick={addRepeatRow}
                disabled={submitting || rowsLoading}
              >
                <FaPlus className="me-1" />
                {addRowLabel}
              </Button>
            ) : null}
          </div>
        </div>

        {submitHidePrimaryButton ? (
          <div className="mt-3 d-flex flex-column gap-3">
            {(() => {
              if (loadFormOnOpen && loadFormScriptLoading) {
                return null;
              }
              const waitingToAutoSubmit =
                submitAutoRun &&
                !result &&
                !loadFormScriptLoading &&
                !loadFormScriptError;
              if (!submitting && !waitingToAutoSubmit) {
                return null;
              }
              const statusLabel = submitting ? submitStatusMessage : 'Starting…';
              return (
                <div
                  className="d-flex align-items-center gap-2 text-muted"
                  role="status"
                  aria-live="polite"
                >
                  <Spinner animation="border" size="sm" aria-hidden="true" />
                  <span>{statusLabel}</span>
                </div>
              );
            })()}
            {showFooterCloseButton ? (
              <div
                className="d-flex"
                style={{ justifyContent: submitToolbar.justify }}
              >
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={onModalClose}
                >
                  {closeButtonLabelFooter}
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div
            className="mt-3 d-flex flex-wrap gap-2 align-items-center"
            style={{ justifyContent: submitToolbar.justify }}
          >
            {showFooterCloseButton ? (
              <Button
                type="button"
                variant="outline-secondary"
                onClick={onModalClose}
                disabled={submitting || rowsLoading}
              >
                {closeButtonLabelFooter}
              </Button>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || rowsLoading || loadFormSubmitBlocked}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" role="status" />
                  Submitting…
                </>
              ) : (
                submitToolbar.label
              )}
            </Button>
          </div>
        )}
        {result ? <DynamicFormResult result={result} /> : null}
      </Form>
    </div>
  );
}

function DynamicFormResult({ result }) {
  if (!result) return null;
  if (result.type === 'pdf-legend') {
    return <PdfLegendResultView data={result} />;
  }
  if (result.type === 'pdf') {
    return <PdfResultView data={result} />;
  }
  return (
    <div className="mt-4">
      <div className="fw-semibold mb-2">Result</div>
      <pre
        className="small p-2 border rounded bg-light"
        style={{ maxHeight: 400, overflow: 'auto' }}
      >
        {JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}

function PdfLegendResultView({ data }) {
  const [showRaw, setShowRaw] = useState(false);
  const tables = Array.isArray(data.tables) ? data.tables : [];
  const stats = data.stats || {};
  const pages = Array.isArray(data.pages) ? data.pages : [];
  const totalItems = tables.reduce((n, t) => n + (t.items ? t.items.length : 0), 0);

  const downloadCsv = () => {
    const rows = [['Table', 'Page', 'Description', 'Make', 'Qty']];
    tables.forEach((t) => {
      (t.items || []).forEach((it) => {
        rows.push([
          t.title || '',
          t.pageNumber ?? '',
          it.description || '',
          it.make || '',
          it.qty || '',
        ]);
      });
    });
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const base = data.fileName ? data.fileName.replace(/\.pdf$/i, '') : 'pdf-legend';
    a.download = `${base}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <div>
          <div className="fw-semibold">Extracted items</div>
          <div className="text-muted small">
            {data.fileName ? <span className="me-2">{data.fileName}</span> : null}
            <span className="me-2">Tables: {tables.length}</span>
            <span className="me-2">Items: {totalItems}</span>
            <span className="me-2">Pages: {data.pageCount ?? pages.length}</span>
            {typeof stats.ocrPages === 'number' ? (
              <span className="me-2">OCR pages: {stats.ocrPages}</span>
            ) : null}
            {typeof stats.durationMs === 'number' ? (
              <span>Took: {stats.durationMs} ms</span>
            ) : null}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-primary" onClick={downloadCsv} disabled={totalItems === 0}>
            Download CSV
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? 'Hide raw OCR' : 'Show raw OCR'}
          </Button>
        </div>
      </div>

      {tables.length === 0 ? (
        <div className="p-3 border rounded text-muted small">
          No matching tables were found. Try re-running with OCR = <b>Always</b>, DPI = <b>400</b>,
          and Layout mode = <b>11 (sparse)</b> or <b>6 (uniform block)</b>.
        </div>
      ) : (
        tables.map((t, ti) => (
          <div key={ti} className="mb-3 border rounded">
            <div className="px-3 py-2 bg-light border-bottom d-flex justify-content-between align-items-center">
              <div className="fw-semibold">{t.title || '(untitled)'}</div>
              <div className="small text-muted">
                Page {t.pageNumber ?? '?'} • {t.items ? t.items.length : 0} item(s)
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-striped mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Description</th>
                    <th style={{ width: 160 }}>Make</th>
                    <th style={{ width: 100 }}>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {(t.items || []).map((it, idx) => (
                    <tr key={idx}>
                      <td className="text-muted">{idx + 1}</td>
                      <td>{it.description}</td>
                      <td>{it.make}</td>
                      <td>{it.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {showRaw ? (
        <div className="mt-3">
          <div className="fw-semibold small mb-1">Raw OCR text (per page)</div>
          <div className="border rounded" style={{ maxHeight: 300, overflow: 'auto' }}>
            {pages.map((p) => (
              <div key={p.pageNumber} className="border-bottom">
                <div className="px-3 py-1 bg-light small fw-semibold">
                  Page {p.pageNumber} — {p.method} ({p.charCount} chars)
                </div>
                <pre
                  className="small m-0 p-2"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {p.text || '(empty)'}
                </pre>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PdfResultView({ data }) {
  const [expanded, setExpanded] = useState({});

  const pages = Array.isArray(data.pages) ? data.pages : [];
  const stats = data.stats || {};
  const info = data.info || {};

  const toggle = (pageNumber) => {
    setExpanded((prev) => ({ ...prev, [pageNumber]: !prev[pageNumber] }));
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
    } catch {
      /* ignore */
    }
  };

  const downloadAll = () => {
    const blob = new Blob([data.fullText || ''], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const base = data.fileName ? data.fileName.replace(/\.pdf$/i, '') : 'pdf-text';
    a.download = `${base}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <div>
          <div className="fw-semibold">PDF extraction result</div>
          <div className="text-muted small">
            {data.fileName ? <span className="me-2">{data.fileName}</span> : null}
            <span className="me-2">Pages: {data.pageCount ?? pages.length}</span>
            {typeof stats.textPages === 'number' ? (
              <span className="me-2">Text pages: {stats.textPages}</span>
            ) : null}
            {typeof stats.ocrPages === 'number' ? (
              <span className="me-2">OCR pages: {stats.ocrPages}</span>
            ) : null}
            {typeof stats.emptyPages === 'number' ? (
              <span className="me-2">Empty: {stats.emptyPages}</span>
            ) : null}
            {typeof stats.durationMs === 'number' ? (
              <span>Took: {stats.durationMs} ms</span>
            ) : null}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={() => copyText(data.fullText)}>
            Copy all text
          </Button>
          <Button size="sm" variant="outline-primary" onClick={downloadAll}>
            Download .txt
          </Button>
        </div>
      </div>

      {info.Title || info.Author || info.CreationDate ? (
        <div className="small text-muted mb-2">
          {info.Title ? <span className="me-3">Title: {info.Title}</span> : null}
          {info.Author ? <span className="me-3">Author: {info.Author}</span> : null}
          {info.CreationDate ? <span>Created: {String(info.CreationDate)}</span> : null}
        </div>
      ) : null}

      <div className="border rounded" style={{ maxHeight: 500, overflow: 'auto' }}>
        {pages.length === 0 ? (
          <div className="p-3 text-muted">No pages extracted.</div>
        ) : (
          pages.map((p) => {
            const isOpen = !!expanded[p.pageNumber];
            return (
              <div key={p.pageNumber} className="border-bottom">
                <div
                  className="d-flex align-items-center justify-content-between px-3 py-2"
                  style={{ cursor: 'pointer', background: '#f8f9fa' }}
                  onClick={() => toggle(p.pageNumber)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className={`bi bi-chevron-${isOpen ? 'down' : 'right'}`}></i>
                    <span className="fw-semibold">Page {p.pageNumber}</span>
                    <span className="badge bg-secondary-subtle text-secondary-emphasis">
                      {p.method}
                    </span>
                    <span className="small text-muted">{p.charCount} chars</span>
                    {typeof p.ocrConfidence === 'number' ? (
                      <span className="small text-muted">
                        OCR: {Math.round(p.ocrConfidence)}%
                      </span>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      copyText(p.text);
                    }}
                  >
                    Copy
                  </Button>
                </div>
                {isOpen ? (
                  <pre
                    className="small m-0 p-3"
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {p.text || '(empty)'}
                  </pre>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
